import SiteCoreLinks from "@/components/SiteCoreLinks";
// /visa-wizard — Thai Visa Eligibility Wizard. Phase P-VISA-WIZARD (2026-06).
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, MessageCircle, Sparkles, ArrowRight } from "lucide-react";
import { VisaWizard, WizardRelatedServices } from "@/components/VisaWizard";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/visa-wizard";
export const TITLE = "วีซ่าไทยแบบไหนเหมาะกับคุณ? Eligibility Wizard 2026 | NYC";
export const DESC =
  "ตอบ 5 คำถามใน 60 วินาที ระบบจะแนะนำวีซ่าไทยที่ใช่ — Tourist · DTV · LTR · Non-O Retirement · Non-O Marriage · Non-B + Work Permit — ส่งภาพเอกสารมาเช็กให้ก่อน ฟรี ไม่คิดค่าปรึกษา";

export const Route = createFileRoute("/visa-wizard")({
  head: () => {
    const url = `${SITE}${PATH}`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/visa-wizard", "th")},
        { name: "description", content: ctaDesc(DESC, "/visa-wizard", "th")},
        { property: "og:title", content: ctaTitle(TITLE, "/visa-wizard", "th")},
        { property: "og:description", content: ctaDesc(DESC, "/visa-wizard", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
        { name: "keywords", content: "วีซ่าไทยแบบไหน, เลือกวีซ่าไทย, DTV vs LTR, Non-O เกษียณ, วีซ่าทำงาน, Eligibility Wizard" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(PATH, "/en/visa-wizard")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "@id": `${url}#tool`,
              name: "Thai Visa Eligibility Wizard",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Any",
              url,
              inLanguage: "th",
              offers: { "@type": "Offer", price: "0", priceCurrency: "THB" },
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
            }),
          },
          faqPageSchema([
            { q: "ระบบ Eligibility Wizard ใช้งานยังไง?", a: "ระบบจะถามคุณ 5 ข้อ ได้แก่ เป้าหมายในการอยู่ไทย ระยะเวลาพัก แผนการทำงาน อายุ 50+ หรือไม่ และหลักฐานการเงิน จากนั้นจะคำนวณคะแนนวีซ่าทั้ง 7 ประเภทและแนะนำตัวเลือกที่เหมาะที่สุดพร้อมเหตุผล ใช้เวลาประมาณ 60 วินาที" },
            { q: "ใช้ทดแทนการปรึกษาทนายความได้ไหม?", a: "Wizard ให้ภาพรวมและแนะนำทิศทาง ไม่ใช่คำปรึกษาทางกฎหมาย เคสที่มีรายละเอียดเฉพาะ เช่น คดีอาญา ภาษีต่างประเทศ หรือสถานะคู่สมรสที่ซับซ้อน ควรปรึกษาทีม NYC โดยตรง" },
            { q: "ผลลัพธ์เก็บข้อมูลของฉันไว้ไหม?", a: "ไม่ — ระบบประมวลผลด้วย Client-side JavaScript ในเบราว์เซอร์ของคุณเท่านั้น ไม่ส่งคำตอบไปยังเซิร์ฟเวอร์ ไม่ใช้ tracking cookie ไม่ใช้ fingerprint ตาม พ.ร.บ.คุ้มครองข้อมูลส่วนบุคคล 2562 ม.24 และ GDPR Art.25 (Privacy by Design) — รีเฟรชหน้าจะหายทันที หากต้องการบันทึกให้กดปุ่ม Download PDF เก็บไว้เอง" },
            { q: "Digital Nomad ควรเลือก DTV หรือ LTR?", a: "ถ้ารายได้ < USD 80K/ปี เลือก DTV (5 ปี 10,000 บาท แสดงเงิน 500K) ถ้ารายได้ ≥ USD 80K/ปีและทำ remote ให้บริษัทต่างประเทศที่จดทะเบียนใน stock exchange เลือก LTR Work-from-Thailand Professional (10 ปี + Digital Work Permit)" },
            { q: "อายุไม่ถึง 50 ใช้ Non-O Retirement ได้ไหม?", a: "ไม่ได้ Non-O Retirement กำหนดอายุ 50 ปีบริบูรณ์ ถ้าอายุน้อยกว่านี้และต้องการพำนักระยะยาว ให้พิจารณา DTV, LTR (HSP/WFTP), Non-O Marriage, หรือ Non-B + Work Permit แทน" },
          ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: `${SITE}/` },
                { "@type": "ListItem", position: 2, name: "Visa Wizard", item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: VisaWizardPage,
});

function VisaWizardPage() {
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-14 pb-10">
        <div className="mx-auto max-w-4xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span><span>Visa Wizard</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Sparkles className="mr-1 inline h-3 w-3" /> AI Eligibility Wizard · 5 คำถาม · ~60 วินาที
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">วีซ่าไทยแบบไหนเหมาะกับคุณ?</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            ตอบคำถามสั้น ๆ 5 ข้อ ระบบจะให้คะแนนความเหมาะสมของวีซ่าไทย 7 ประเภท (Tourist · DTV · LTR · Non-O Retirement · Non-O Marriage · Non-B + Work Permit · Non-ED) พร้อมเหตุผล ค่าธรรมเนียม และระยะเวลาดำเนินการ
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">ระบบช่วยเลือกวีซ่าทำงานยังไง?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              ระบบถามโปรไฟล์ของคุณ 5 ข้อ (เป้าหมาย, ระยะเวลาพัก, งาน, อายุ, หลักฐานการเงิน) แล้วคำนวณคะแนนของวีซ่าทั้ง 7 ประเภท พร้อมเหตุผลและข้อกำหนดเงินทุน ใช้เวลาประมาณ 60 วินาที — ผลลัพธ์ประมวลผลในเบราว์เซอร์ของคุณ ไม่มีการเก็บข้อมูลส่วนตัว
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092 242 9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/visa-matrix" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              ตารางเทียบ 7 วีซ่า <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-10">
        <div className="mx-auto max-w-3xl">
          <VisaWizard locale="th" />
          <p className="mt-4 text-center text-[11px] text-muted-foreground">
            ⚖️ Wizard นี้ให้ภาพรวมเบื้องต้น ไม่ใช่คำปรึกษาทางกฎหมาย เคสที่ซับซ้อนกรุณาปรึกษาทีม NYC โดยตรง
          </p>
        </div>
      </section>

      <WizardRelatedServices locale="th" />
      <TrustLayer variant="compact" />
          <SiteCoreLinks />
    </main>
  );
}
